import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ToastContainer, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RequireAuth from "./config/requireAuth";
import Layout from "./components/layout/layout";
import HomePage from "./pages/HomePage";
import TourListPage from "./pages/TourListPage";
import TourDetailPage from "./pages/TourDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import InvoicePage from "./pages/InvoicePage";
import BlogListPage from "./pages/BlogListPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import BlogCreate from "./pages/BlogCreate";
import BlogUpdate from "./pages/BlogUpdate";
import NotFoundPage from "./pages/404-page";
import ContactUsPage from "./pages/ContactUsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import CustomerTourBooking from "./pages/CustomerTourBooking";
import MyProfilePage from "./pages/MyProfilePage";
import WishlistPage from "./pages/WishlistPage";
import UserLayout from "./pages/UserLayout";
import Review from "./pages/ReviewPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import RequireNotUser from "./config/requireNotUser";
import UnauthorizedPage from "./pages/401-page";
import ForbiddenPage from "./pages/403-page";
import ServerErrorPage from "./pages/500-page";
import UpdateUser from "./components/my-profile/update-user";
import ChangePassword from "./components/my-profile/change-password";
import TourBookingPage from "./pages/TourBookingPage";
import TourBookingConfirmationPage from "./pages/TourBookingConfirmationPage";
import TourAssignPage from "./pages/TourAssignPage";
import AssignTourGuide from "./components/tour/assign-tourGuide";
import TourGuideAssignments from "./components/tour-guide/tour-guide-assign";
import ViewTourGuide from "./components/tour-guide/view-tour-guide";
import ReplaceTourGuideMain from "./components/tour-guide/replace-main-guide";
import ReplaceTourGuideBackup from "./components/tour-guide/replace-backup-guide";

import ChatPage from "./pages/ChatPage";
import CancelPendingBookings from "./components/tour-booking/get-booking-cancel";
import TourSearchPage from "./components/tour/tour-search";
import ChatBot from "./components/chat-bot-AI/chat-bot";
import MyBlogPage from "./pages/MyBlogPage";
import ManageBlogPage from "./pages/ManageBlogPage";
import CreateTourPersonalized from "./components/personalized-tour/create-tour-personalized";
import MyPersonalizedTourPage from "./pages/MyTourPersonalizedPage";
import MiniGamePage from "./pages/MiniGamePage";
import ManageGamePage from "./pages/ManageGamePage";
import ManageSettingGamePage from "./pages/ManageSettingGamePage";
import { Analytics } from "@vercel/analytics/react";

const App = () => {
  return (
    <>
      <Analytics />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {[
            "/register",
            "/confirm-email",
            "/forgot-password",
            "/reset-password",
          ].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireNotUser>
                  {path === "/register" && <RegisterPage />}
                  {path === "/confirm-email" && <ConfirmEmailPage />}
                  {path === "/forgot-password" && <ForgotPasswordPage />}
                  {path === "/reset-password" && <ChangePasswordPage />}
                </RequireNotUser>
              }
            />
          ))}

          {[
            "/my-profile",
            "/update-user",
            "/change-password",
            "/chat",
            "/chat-bot",
            "/create-tour-personalized",
            "/my-tour-personalized",
            "/mini-game",
          ].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAuth
                  allowedRoles={["Customer", "Admin", "Tour guide", "Staff"]}
                >
                  {path === "/my-profile" && <MyProfilePage />}
                  {path === "/update-user" && <UpdateUser />}
                  {path === "/change-password" && <ChangePassword />}
                  {path === "/chat" && <ChatPage />}
                  {path === "/chat-bot" && <ChatBot />}
                  {path === "/create-tour-personalized" && (
                    <CreateTourPersonalized />
                  )}
                  {path === "/my-tour-personalized" && (
                    <MyPersonalizedTourPage />
                  )}
                  {path === "/mini-game" && <MiniGamePage />}
                </RequireAuth>
              }
            />
          ))}

          {["/blog-create", "/my-blog"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAuth
                  allowedRoles={["Customer", "Admin", "Tour guide", "Staff"]}
                >
                  {/* <Route path="/blog-create" element={<BlogCreate />} />
                    <Route path="/blog-update/:blogId" element={<BlogUpdate />} /> */}
                  {path === "/my-blog" && <MyBlogPage />}
                  {path === "/blog-create" && <BlogCreate />}
                </RequireAuth>
              }
            />
          ))}

          <Route
            path="/blog-update/:blogId"
            element={
              <RequireAuth
                allowedRoles={["Customer", "Admin", "Tour guide", "Staff"]}
              >
                <BlogUpdate />
              </RequireAuth>
            }
          />
          {[
            "/tour-assign",
            "/replace-tour-guide-main",
            "/replace-tour-guide-backup",
            "/tour-booking-cancel",
            "/manage-blog",
            "/manage-mini-game",
            "/manage-setting-game",
          ].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAuth allowedRoles={["Staff"]}>
                  {path === "/tour-assign" && <TourAssignPage />}
                  {path === "/replace-tour-guide-main" && (
                    <ReplaceTourGuideMain />
                  )}
                  {path === "/replace-tour-guide-backup" && (
                    <ReplaceTourGuideBackup />
                  )}
                  {path === "/tour-booking-cancel" && <CancelPendingBookings />}
                  {path === "/manage-blog" && <ManageBlogPage />}
                  {path === "/manage-mini-game" && <ManageGamePage />}
                  {path === "/manage-setting-game" && <ManageSettingGamePage />}
                </RequireAuth>
              }
            />
          ))}

          {["/tour-guide-assign"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAuth allowedRoles={["Tour guide"]}>
                  {path === "/tour-guide-assign" && <TourGuideAssignments />}
                </RequireAuth>
              }
            />
          ))}

          <Route
            path="/assign-tour-guide/:tourAssignmentId"
            element={
              <RequireAuth allowedRoles={["Staff"]}>
                <AssignTourGuide />
              </RequireAuth>
            }
          />

          <Route
            path="/view-tour-guide/:tourAssignmentId"
            element={
              <RequireAuth allowedRoles={["Staff"]}>
                <ViewTourGuide />
              </RequireAuth>
            }
          />

          <Route path="/" element={<HomePage />} />
          <Route path="/tour-search" element={<TourSearchPage />} />
          <Route path="/*" element={<NotFoundPage />} />
          <Route path="/tour-list" element={<TourListPage />} />
          <Route path="/tour-detail/:tourId" element={<TourDetailPage />} />

          {/* <Route path="/blog" element={<BlogListPage />} /> */}
          {/* <Route path="/blog-detail" element={<BlogDetailPage />} /> */}

          <Route
            path="/"
            element={
              <RequireAuth
                allowedRoles={["Customer", "Staff", "Admin", "Tour guide"]}
              >
                <UserLayout />
              </RequireAuth>
            }
          >
            {/* Các route con */}
            <Route path="contact-us" element={<ContactUsPage />} />
            <Route path="invoice" element={<InvoicePage />} />
            <Route
              path="customer-tour-booking"
              element={<CustomerTourBooking />}
            />
            <Route path="review" element={<Review />} />
            <Route path="wishlist" element={<WishlistPage />} />
            <Route path="contact-us" element={<ContactUsPage />} />
            <Route path="booking" element={<TourBookingPage />} />
            <Route
              path="booking-confirmation"
              element={<TourBookingConfirmationPage />}
            />
          </Route>

          <Route path="/error-401" element={<UnauthorizedPage />} />
          <Route path="/error-403" element={<ForbiddenPage />} />
          <Route path="/error-404" element={<NotFoundPage />} />
          <Route path="/error-500" element={<ServerErrorPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog-detail/:blogId" element={<BlogDetailPage />} />
        </Routes>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Bounce}
      />
    </>
  );
};

export default App;
